import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useState, ChangeEvent } from 'react';
import { IndeterminateCheckbox } from '@/entities/Table';
import { ActionsHeader } from '@/entities/ActionsHeader/ui/ActionsHeader';
import { TextCell } from '@/entities/TextCell';
import { UsualTableItem } from '@/shared/model/types/table';
import { TextHeader } from '@/entities/TextHeader';
import { EditableCell } from '@/shared/ui/EditableCell/EditableCell';
import { dateFormatFromBack } from '@/shared/lib/utils/dateFormat';
import { fieldLabels } from '@/entities/User/model/constants';
import { ActionsCell } from '@/entities/ActionsCell/ui/ActionsCell';
import { useLazyGetRegionsQuery } from '@/features/RegionSearch';
import { RegionInlineSearch } from '@/shared/ui/RegionInlineSearch';

import { Toggle } from '@/shared/ui/Toggle/Toggle';
import tableStyle from '../ui/UsualTable.module.scss';
import { getAccountDisplayName } from '@/shared/lib/utils/accountPresentation';

type HandleSaveFn = (id: number, field: string, value: string | null, blockReason?: string | null) => Promise<void>;
type HandleOpenLogFn = (userId: number, userName: string) => void;

const RegionCell = ({
  id,
  initialValue,
  onSave,
}: {
  id: number;
  initialValue: string | null;
  onSave: (value: string | null) => Promise<void>;
}) => {
  const [value, setValue] = useState<string>(initialValue || '');
  const [triggerRegions] = useLazyGetRegionsQuery();

  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const save = useCallback(
    async (val: string | null) => {
      const normalized = val && val.length > 0 ? val : '-';
      await onSave(normalized);
    },
    [onSave],
  );

  const validateRegion = useCallback(
    async (val: string) => {
      if (val && val !== '-') {
        const res = await triggerRegions(val);
        const first = res?.data?.regions?.[0]?.split('_')[1];
        if (first === val) {
          await save(val);
        }
        return;
      }
      await save('-');
    },
    [triggerRegions, save],
  );

  const onInlineChange = useCallback((e: string | ChangeEvent<HTMLInputElement>) => {
    const next = typeof e === 'string' ? e : e.target.value;
    setValue(next);
  }, []);

  return (
    <RegionInlineSearch
      value={value}
      onChange={onInlineChange}
      onPick={(v) => {
        void save(v);
      }}
      onClose={() => {
        void validateRegion(value);
      }}
      placeholder="Введите регион"
    />
  );
};

export const getUsualTableColumns = (
  handleSave: HandleSaveFn,
  handleOpenLog: HandleOpenLogFn,
  onToggleActive?: (id: number, currentActive: boolean) => void,
): ColumnDef<UsualTableItem>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        isHeader
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        {...{
          checked: row.getIsSelected(),
          disabled: !row.getCanSelect(),
          indeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      thStyle: {
        position: 'sticky',
        left: 0,
        zIndex: 'var(--z-sticky)',
        backgroundColor: 'var(--white-100)',
        WebkitMaskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
        maskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
        WebkitMaskClip: 'padding-box',
        maskClip: 'padding-box',
      },
      tdStyle: {
        position: 'sticky',
        left: 0,
        zIndex: 'var(--z-sticky)',
        backgroundColor: 'var(--white-100)',
        WebkitMaskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
        maskImage:
          'linear-gradient(to right, var(--black-100) calc(100% - var(--sticky-fade-width)), transparent 100%)',
      },
    },
  },
  {
    header: fieldLabels.lastName,
    accessorKey: 'lastName',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={getAccountDisplayName(row.original)} />
      ) : (
        <EditableCell
          value={row.getValue('lastName') as string | null}
          field="lastName"
          type="string"
          onSave={(value) => handleSave(row.original.id, 'lastName', value)}
        />
      ),
  },
  {
    header: fieldLabels.firstName,
    accessorKey: 'firstName',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={null} />
      ) : (
        <EditableCell
          value={row.getValue('firstName') as string | null}
          field="firstName"
          type="string"
          onSave={(value) => handleSave(row.original.id, 'firstName', value)}
        />
      ),
  },
  {
    header: fieldLabels.patronymic,
    accessorKey: 'patronymic',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={null} />
      ) : (
        <EditableCell
          value={row.getValue('patronymic') as string | null}
          field="patronymic"
          type="string"
          onSave={(value) => handleSave(row.original.id, 'patronymic', value)}
        />
      ),
  },
  {
    header: fieldLabels.nickname,
    accessorKey: 'nickname',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={null} />
      ) : (
        <EditableCell
          value={row.getValue('nickname') as string | null}
          field="nickname"
          type="string"
          onSave={(value) => handleSave(row.original.id, 'nickname', value)}
        />
      ),
  },
  {
    header: () => <TextHeader>{fieldLabels.phoneNumber}</TextHeader>,
    accessorKey: 'phoneNumber',
    cell: (props) => <TextCell value={props.getValue() as string | null} />,
    enableSorting: false,
  },
  {
    header: fieldLabels.birthDate,
    accessorKey: 'birthDate',
    cell: ({ row }) => {
      const rawDate = row.getValue('birthDate') as string | null;
      const formattedDate = rawDate ? dateFormatFromBack(rawDate) : null;

      if (row.original.accountStatus === 'deleted') {
        return <TextCell value={formattedDate} />;
      }

      return (
        <EditableCell
          value={formattedDate}
          field="birthDate"
          type="date"
          onSave={(value) => handleSave(row.original.id, 'birthDate', value)}
        />
      );
    },
  },
  {
    header: fieldLabels.region,
    accessorKey: 'region',
    meta: {
      thStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
      tdStyle: { width: '26ch', minWidth: '22ch', maxWidth: '32ch' },
    },
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={row.getValue('region') as string | null} />
      ) : (
        <RegionCell
          id={row.original.id}
          initialValue={(row.getValue('region') as string | null) ?? ''}
          onSave={(val) => handleSave(row.original.id, 'region', val)}
        />
      ),
  },
  {
    header: fieldLabels.team,
    accessorKey: 'team',
    cell: (props) => <TextCell value={props.getValue() as string | null} />,
  },
  {
    header: () => <TextHeader>{fieldLabels.vkLink}</TextHeader>,
    accessorKey: 'vkLink',
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={row.getValue('vkLink') as string | null} />
      ) : (
        <EditableCell
          value={row.getValue('vkLink') as string | null}
          field="vkLink"
          type="string"
          onSave={(value) => handleSave(row.original.id, 'vkLink', value)}
        />
      ),
    enableSorting: false,
  },
  {
    header: fieldLabels.rate,
    accessorKey: 'rate',
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={row.getValue('rate') as string | number | null} />
      ) : (
        <EditableCell
          value={row.getValue('rate') as string | null}
          field="rate"
          type="select"
          onSave={(value) => handleSave(row.original.id, 'rate', value)}
        />
      ),
  },
  {
    header: fieldLabels.qualification,
    accessorKey: 'qualification',
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={row.getValue('qualification') as string | null} />
      ) : (
        <EditableCell
          value={row.getValue('qualification') as string | null}
          field="qualification"
          type="select"
          onSave={(value) => handleSave(row.original.id, 'qualification', value)}
        />
      ),
  },
  {
    header: fieldLabels.status,
    accessorKey: 'status',
    cell: ({ row }) =>
      row.original.accountStatus === 'deleted' ? (
        <TextCell value={row.getValue('status') as string | null} />
      ) : (
        <EditableCell
          value={row.getValue('status') as string | null}
          field="status"
          type="select"
          onSave={(value) => handleSave(row.original.id, 'status', value)}
        />
      ),
  },
  {
    header: fieldLabels.isActive,
    accessorKey: 'isActive',
    cell: ({ row }) =>
      row.original.accountStatus !== 'active' && row.original.accountStatus !== undefined ? (
        <TextCell value={null} />
      ) : (
        <div className={tableStyle.toggleCell}>
          <Toggle
            isActive={row.original.isActive}
            onToggle={() => onToggleActive?.(row.original.id, row.original.isActive)}
          />
        </div>
      ),
  },
  {
    header: () => <ActionsHeader />,
    accessorKey: 'delete',
    cell: (row) => <ActionsCell row={row} onOpenLog={(id, name) => handleOpenLog(id, name)} />,
    enableSorting: false,
  },
];
